/**
 * Lightweight i18n · ใช้ prefs.language เป็น source-of-truth
 *   const t = useT()
 *   {{ t('common.filter') }}
 *   {{ t('markets.count', { n: 25 }) }}
 *
 * Backend i18n (Accept-Language) จัดการ dynamic data (sector names, AI text, news titles)
 * ที่นี่จัดการ static UI strings เท่านั้น
 */
import { computed, type ComputedRef } from 'vue'
import { usePrefsStore } from '@/stores/prefs'

const TH = {
  // Common
  'common.all':           'ทั้งหมด',
  'common.filter':        'ตัวกรอง',
  'common.apply':         'ใช้ตัวกรอง',
  'common.reset':         'รีเซ็ต',
  'common.cancel':        'ยกเลิก',
  'common.close':         'ปิด',
  'common.loading':       'กำลังโหลด…',
  'common.empty':         'ไม่มีรายการ',
  'common.retry':         'ลองใหม่',
  'common.back':          'ย้อนกลับ',
  'common.search':        'ค้นหา',
  'common.save':          'บันทึก',
  'common.edit':          'แก้ไข',
  'common.delete':        'ลบ',

  // Nav (BottomTabBar)
  'nav.home':             'หน้าหลัก',
  'nav.markets':          'ตลาด',
  'nav.watchlist':        'ติดตาม',
  'nav.profile':          'ฉัน',

  // Asset kinds
  'kind.stock':           'หุ้น',
  'kind.etf':             'ETF',
  'kind.crypto':          'Crypto',
  'kind.stock.unit':      'หุ้น',
  'kind.etf.unit':        'ETF',
  'kind.crypto.unit':     'เหรียญ',

  // Markets page
  'markets.title':        'ตลาด',
  'markets.greeting':     'ค้นหาในตลาด',
  'markets.count':        '{n} {unit}',
  'markets.inSector':     'ในหมวด {name}',
  'markets.sortedBy':     'เรียงตาม {field}',
  'markets.noItems':      'ไม่มีรายการในหมวดนี้',

  // Filter sheet
  'filter.title':         'ตัวกรอง',
  'filter.sort':          'เรียงตาม',
  'filter.sort.change_desc':  '% เปลี่ยน (มาก → น้อย)',
  'filter.sort.change_asc':   '% เปลี่ยน (น้อย → มาก)',
  'filter.sort.marketCap':    'มูลค่าตลาด',
  'filter.sort.volume':       'ปริมาณซื้อขาย',
  'filter.sort.price_desc':   'ราคา (สูง → ต่ำ)',
  'filter.sort.price_asc':    'ราคา (ต่ำ → สูง)',
  'filter.marketCap':         'มูลค่าตลาด',
  'filter.marketCap.any':     'ทั้งหมด',
  'filter.marketCap.mega':    'Mega Cap (>$200B)',
  'filter.marketCap.large':   'Large Cap ($10B-$200B)',
  'filter.marketCap.mid':     'Mid Cap ($2B-$10B)',
  'filter.marketCap.small':   'Small Cap ($300M-$2B)',
  'filter.marketCap.micro':   'Micro Cap (<$300M)',
  'filter.change':            'เปลี่ยนแปลงวันนี้',
  'filter.change.any':        'ทั้งหมด',
  'filter.change.gainers':    'กำลังขึ้น',
  'filter.change.losers':     'กำลังลง',
  'filter.change.big_gainers':'ขึ้นแรง (>5%)',
  'filter.change.big_losers': 'ลงแรง (<-5%)',
  'filter.price':             'ช่วงราคา',
  'filter.price.any':         'ทั้งหมด',
  'filter.price.under10':     '< $10',
  'filter.price.10to100':     '$10-$100',
  'filter.price.100to500':    '$100-$500',
  'filter.price.over500':     '> $500',

  // Sort field labels
  'sort.changePct':       '%เปลี่ยน',
  'sort.marketCap':       'มูลค่าตลาด',
  'sort.volume':          'ปริมาณ',
  'sort.price':           'ราคา',

  // Profile / Settings page
  'profile.greeting':              'บัญชีของคุณ',
  'profile.title':                 'โปรไฟล์',
  'profile.settings':              'ตั้งค่า',
  'profile.stats.favorites':       'หุ้นโปรด',
  'profile.stats.alerts':          'แจ้งเตือน',
  'profile.stats.portfolio':       'พอร์ต',
  'profile.stats.investment':      'การลงทุน',
  'profile.section.settings':      'การตั้งค่า',
  'profile.section.account':       'บัญชีและตัวช่วย',
  'profile.item.region':           'ภูมิภาคหุ้น',
  'profile.item.language':         'ภาษา',
  'profile.item.language.value':   'ไทย (Thai)',
  'profile.item.theme':            'ธีม',
  'profile.item.theme.value':      'มืด (Dark)',
  'profile.item.notifications':    'การแจ้งเตือน',
  'profile.item.notifications.value': 'Telegram · ในแอป',
  'profile.item.privacy':          'ความเป็นส่วนตัว',
  'profile.item.privacy.value':    'ข้อมูลและความปลอดภัย',
  'settings.region.title':         'เลือกภูมิภาคหุ้น',
  'settings.region.hint':          'ภูมิภาคจะกรองหุ้นในตลาด · ETF/Crypto เป็น global ไม่ขึ้นกับการตั้งค่านี้',

  'region.us': '🇺🇸 สหรัฐอเมริกา',
  'region.gb': '🇬🇧 อังกฤษ',
  'region.de': '🇩🇪 เยอรมนี',
  'region.jp': '🇯🇵 ญี่ปุ่น',
  'region.hk': '🇭🇰 ฮ่องกง',
  'region.ca': '🇨🇦 แคนาดา',
  'region.au': '🇦🇺 ออสเตรเลีย',
  'region.fr': '🇫🇷 ฝรั่งเศส',
  'region.in': '🇮🇳 อินเดีย',
  'region.br': '🇧🇷 บราซิล',
  'region.sg': '🇸🇬 สิงคโปร์',
  'region.kr': '🇰🇷 เกาหลีใต้',
  'region.tw': '🇹🇼 ไต้หวัน',
  'region.ch': '🇨🇭 สวิตเซอร์แลนด์',

  'profile.item.help':             'ช่วยเหลือ & FAQ',
  'profile.item.terms':            'เงื่อนไขการใช้งาน',
  'profile.item.logout':           'ออกจากระบบ',
  'profile.footer':                'Valryux Stocks v0.1.0 · ขับเคลื่อนโดย NVIDIA NIM + Yahoo Finance',

  // Settings sheets
  'settings.title':                'การตั้งค่า',
  'settings.language':             'ภาษา',
  'settings.language.picker_title':'เลือกภาษา',
  'settings.language.picker_hint': 'เปลี่ยนภาษาจะมีผลกับข้อความใหม่ ๆ',
  'settings.theme.title':          'เลือกธีม',
  'settings.theme.dark':           'มืด',
  'settings.theme.light':          'สว่าง',
  'settings.theme.system':         'ตามระบบ',
  'settings.theme.hint':           'ธีมจะปรับอัตโนมัติตามเวลา',
  'settings.notifications.title':  'การแจ้งเตือน',
  'settings.notifications.telegram':       'Telegram',
  'settings.notifications.telegram.hint':  'ส่งข้อความผ่านบอท',
  'settings.notifications.inapp':          'ในแอป',
  'settings.notifications.inapp.hint':     'แสดงในหน้าแจ้งเตือน',
  'settings.privacy.title':        'ความเป็นส่วนตัว',
  'settings.privacy.dataUsage':    'การใช้ข้อมูล',
  'settings.privacy.dataUsage.body': 'แอปนี้เก็บแค่ Telegram ID และรายการติดตามของคุณ · ไม่มี cookies tracking · ไม่ขายข้อมูลให้บุคคลที่สาม',
  'settings.privacy.delete':       'ลบบัญชี',
  'settings.privacy.delete.body':  'ส่งคำขอลบบัญชีพร้อมข้อมูลทั้งหมด · จะดำเนินการภายใน 7 วัน',
  'settings.help.title':           'ช่วยเหลือ & FAQ',
  'settings.help.q1':              'ราคาแม่นแค่ไหน?',
  'settings.help.a1':              'ข้อมูลจาก Yahoo Finance · delay ~15 นาทีสำหรับ free tier',
  'settings.help.q2':              'AI วิเคราะห์น่าเชื่อถือไหม?',
  'settings.help.a2':              'AI ใช้ข้อมูล Yahoo + technical indicators · ไม่ใช่คำแนะนำการลงทุน',
  'settings.help.q3':              'ตั้งแจ้งเตือนได้กี่อัน?',
  'settings.help.a3':              'ไม่จำกัด · แจ้งผ่าน Telegram และในแอป',
  'settings.terms.title':          'เงื่อนไขการใช้งาน',
  'settings.terms.body':           'การใช้งานแอปนี้ถือว่ายอมรับว่า: (1) ข้อมูลเพื่อการศึกษาเท่านั้น · ไม่ใช่คำแนะนำการลงทุน · (2) ผู้พัฒนาไม่รับผิดชอบต่อการตัดสินใจซื้อขาย · (3) ข้อมูลอาจ delay หรือไม่ครบถ้วน · โปรดตรวจสอบจากแหล่งทางการก่อนตัดสินใจ',

  // Common extras
  'common.share':                 'แชร์',
  'common.follow':                'ติดตาม',
  'common.unfollow':              'เลิกติดตาม',
  'common.followed':              '✓ ติดตามแล้ว',
  'common.followAdd':             '+ ติดตาม',
  'common.history':               'ประวัติ',
  'common.add':                   'เพิ่ม',
  'common.clearAll':              'ล้างทั้งหมด',
  'common.clear':                 'ล้าง',
  'common.or':                    'หรือ',
  'common.viewSource':            'ดูต้นฉบับ ›',
  'common.openInWeb':             'เปิดในเว็บแทน',
  'common.source':                'แหล่ง',
  'common.refresh':               'ใหม่',
  'common.thinking':              'กำลังคิด…',

  // Months (Thai)
  'month.1':  'มกราคม',  'month.2':  'กุมภาพันธ์', 'month.3':  'มีนาคม',
  'month.4':  'เมษายน',  'month.5':  'พฤษภาคม',  'month.6':  'มิถุนายน',
  'month.7':  'กรกฎาคม', 'month.8':  'สิงหาคม',   'month.9':  'กันยายน',
  'month.10': 'ตุลาคม',  'month.11': 'พฤศจิกายน', 'month.12': 'ธันวาคม',

  // Weekdays (Sun=0)
  'weekday.0': 'อาทิตย์', 'weekday.1': 'จันทร์', 'weekday.2': 'อังคาร', 'weekday.3': 'พุธ',
  'weekday.4': 'พฤหัส',  'weekday.5': 'ศุกร์',  'weekday.6': 'เสาร์',

  // Earnings Calendar
  'cal.greeting':         'ปฏิทินผลประกอบการ',
  'cal.title':            'Earnings Calendar',
  'cal.entryTitle':       'ปฏิทินผลประกอบการ',
  'cal.entrySubtitle':    'วันรายงานของหุ้นใน watchlist + portfolio',
  'cal.source.all':       'ทั้งหมด',
  'cal.source.watchlist': 'Watchlist',
  'cal.source.portfolio': 'Portfolio',
  'cal.rangeDays':        'ภายใน {n} วัน',
  'cal.today':            'วันนี้',
  'cal.tomorrow':         'พรุ่งนี้',
  'cal.yesterday':        'เมื่อวาน',
  'cal.inDays':           'อีก {n} วัน',
  'cal.daysAgo':          'ผ่านมา {n} วัน',
  'cal.empty':            'ยังไม่มีกำหนดรายงานในช่วงนี้',
  'cal.emptyHint':        'ลองเพิ่มหุ้นใน watchlist หรือเปิด portfolio',
  'cal.summary':          'พบ {n} รายการ ใน {days} วันถัดไป',

  // Stock detail page
  'stock.aria.share':             'แชร์',
  'stock.aria.alarm':             'ตั้งแจ้งเตือน',
  'stock.setAlert':               'ตั้งแจ้งเตือน',
  'stock.priceUnavailable':       'ข้อมูลราคาไม่พร้อม · ลองรีเฟรชหน้านี้',
  'stock.stat.open':              'เปิด',
  'stock.stat.high':              'สูง',
  'stock.stat.low':               'ต่ำ',
  'stock.stat.preMarket':         'ก่อนเปิดตลาด',
  'stock.stat.postMarket':        'หลังตลาดปิด',
  'stock.stat.marketClosed':      'ปิดทำการ',
  'stock.stat.realtime':          'เรียลไทม์',

  'stock.tab.chart':              'กราฟ',
  'stock.tab.company':            'บริษัท',
  'stock.tab.financials':         'งบการเงิน',
  'stock.tab.earning':            'Earning',
  'stock.tab.news':               'ข่าว',

  // Score block
  'stock.score.title':            'คะแนนรวมจาก Yahoo Finance',
  'stock.score.loading':          'กำลังโหลดคะแนนจาก Yahoo Finance…',
  'stock.score.empty':            'ยังไม่มีคะแนนสำหรับ {sym}',
  'stock.score.growth':           'เติบโต',
  'stock.score.value':            'มูลค่า',
  'stock.score.momentum':         'โมเมนตัม',
  'stock.score.quality':          'คุณภาพ',
  'stock.score.sentiment':        'Sentiment',
  'stock.rec.strong_buy':         'ซื้อแรง',
  'stock.rec.buy':                'ซื้อ',
  'stock.rec.hold':               'ถือ',
  'stock.rec.sell':               'ขาย',
  'stock.rec.strong_sell':        'ขายแรง',

  // Chart states
  'stock.chart.loading':          'กำลังโหลดกราฟ…',
  'stock.chart.empty':            'ข้อมูลกราฟไม่พร้อม',

  // AI block
  'stock.ai.title':               'วิเคราะห์ด้วย AI',
  'stock.ai.summary':             'สรุปจาก AI',
  'stock.ai.emptyHint':           'ยังไม่มีบทวิเคราะห์สำหรับ {sym} · กดปุ่ม "↻ ใหม่" เพื่อให้ AI วิเคราะห์',
  'stock.ai.streaming':           'AI กำลังวิเคราะห์…',
  'stock.ai.source':              'แหล่งข้อมูล: Yahoo Finance · NVIDIA NIM ·',
  'stock.ai.disclaimer':          'วิเคราะห์ไม่ใช่คำแนะนำการลงทุน',

  // Company tab
  'stock.company.empty':          'ข้อมูลบริษัทไม่พร้อม',
  'stock.company.ceo':            'CEO',
  'stock.company.founded':        'ก่อตั้ง',
  'stock.company.hq':             'สำนักงานใหญ่',
  'stock.company.employees':      'พนักงาน',
  'stock.company.exchange':       'Exchange',
  'stock.company.sector':         'Sector',
  'stock.company.industry':       'Industry',
  'stock.company.marketCap':      'Market Cap',
  'stock.company.tagsTitle':      'หมวด / Industry',
  'stock.company.shareholders':   'ผู้ถือหุ้นรายใหญ่',

  // Financials tab
  'stock.fin.empty':              'ข้อมูลงบการเงินไม่พร้อม',
  'stock.fin.revenue':            'รายได้',
  'stock.fin.eps':                'EPS',
  'stock.fin.grossMargin':        'Gross Margin',
  'stock.fin.opMargin':           'Op Margin',
  'stock.fin.history':            'รายได้ย้อนหลัง 4 ไตรมาส',
  'stock.fin.income':             'งบกำไรขาดทุน',
  'stock.fin.grossProfit':        'กำไรขั้นต้น',
  'stock.fin.netIncome':          'กำไรสุทธิ',

  // Earnings tab
  'stock.earn.empty':             'ข้อมูล Earnings ไม่พร้อม',
  'stock.earn.next':              'รายงานผลถัดไป',
  'stock.earn.latest':            'รายงานผลล่าสุด',
  'stock.earn.daysUnit':          'วัน',
  'stock.earn.passedAgo':         'ผ่านมา {n} วัน',
  'stock.earn.last4q':            'ผลย้อนหลัง {n} ไตรมาส',
  'stock.earn.consensus':         'Analyst Consensus',
  'stock.earn.consensus.nodes':   '· {n} nodes',
  'stock.earn.targetMean':        'ราคาเป้าหมายเฉลี่ย',
  'stock.earn.upside':            '{pct}% upside',
  'stock.earn.buyPct':            'ซื้อ {pct}%',
  'stock.earn.holdPct':           'ถือ {pct}%',
  'stock.earn.sellPct':           'ขาย {pct}%',
  'stock.fin.quarterly':          'รายไตรมาส',
  'stock.fin.annual':             'รายปี',
  'stock.fin.trend':              'แนวโน้ม',
  'stock.fin.forecast':           'คาดการณ์',
  'stock.fin.estimateLabel':      'ค่าเฉลี่ยจากนักวิเคราะห์',
  'stock.earn.beatsMisses':       'Beats / Misses ย้อนหลัง',
  'stock.earn.beat':              'Beat',
  'stock.earn.miss':              'Miss',
  'stock.earn.confirmed':         'ยืนยันแล้ว',
  'stock.earn.estimated':         'คาดการณ์',
  'stock.earn.analystCount':      '{n} นักวิเคราะห์',
  'stock.analyst.recentRatings':  'มุมมองล่าสุดจากนักวิเคราะห์',

  // News tab
  'stock.news.sentiment.bull':    'เชิงบวก',
  'stock.news.sentiment.bear':    'เชิงลบ',
  'stock.news.sentiment.neutral': 'เป็นกลาง',
  'stock.news.sentiment.summary': 'Sentiment {tone} · {n} ข่าว',
  'stock.news.sentiment.breakdown': 'บวก {pos} · กลาง {neu} · ลบ {neg}',
  'stock.news.filter.all':        'ทั้งหมด',
  'stock.news.filter.positive':   'บวก',
  'stock.news.filter.neutral':    'กลาง',
  'stock.news.filter.negative':   'ลบ',
  'stock.news.empty':             'ยังไม่มีข่าว',
  'stock.news.emptyFilter':       'ไม่มีข่าว{tone}',

  // Article reader
  'stock.article.loading':        'กำลังโหลดบทความ…',
  'stock.article.paywall':        'บทความนี้ต้องสมัครสมาชิกที่ต้นทาง (paywall)',
  'stock.article.unreadable':     'อ่านบทความไม่ได้ในแอป',
  'stock.article.failed':         'โหลดบทความไม่สำเร็จ',

  // Watchlist
  'wl.greeting':                  'รายการของคุณ',
  'wl.title':                     'ติดตาม',
  'wl.aria.filter':               'ตัวกรอง',
  'wl.aria.add':                  'เพิ่ม',
  'wl.alertsActive':              'การแจ้งเตือนที่เปิดอยู่',
  'wl.alertsTotal':               'ใน {n} รายการ',
  'wl.tab.favorites':             'หุ้นโปรด',
  'wl.tab.alerts':                'การแจ้งเตือน',
  'wl.favorites.empty':           'ยังไม่มีหุ้นที่ติดตาม',
  'wl.alerts.active':             'เปิดอยู่',
  'wl.alerts.inactive':           'ปิดอยู่',
  'wl.alerts.countItems':         '{n} รายการ',
  'wl.alert.target':              'เป้าหมาย',
  'wl.alert.current':             'ปัจจุบัน',
  'wl.alert.near':                'ใกล้ trigger',
  'wl.alert.distance':            'ระยะถึงเป้า',
  'wl.alert.closedTarget':        'เป้า {v} · ปิดไว้',
  'wl.alert.cond.gte':            'ราคา ≥',
  'wl.alert.cond.lte':            'ราคา ≤',
  'wl.alert.cond.change':         '% เปลี่ยน',
  'wl.alert.cond.breakoutR':      'ทะลุ R',
  'wl.alert.cond.breakoutS':      'ทะลุ S',
  'wl.alert.text.autoResist':     'แนวต้านอัตโนมัติ',
  'wl.alert.text.autoSupport':    'แนวรับอัตโนมัติ',
  'wl.alert.text.perDay':         '{v}% / วัน',
  // Alert form
  'notif.detail.title':           'รายละเอียดแจ้งเตือน',
  'notif.detail.alertOn':         'เปิดอยู่',
  'notif.detail.alertOff':        'ปิด',
  'notif.detail.manage':          'จัดการแจ้งเตือน',
  'notif.detail.openStock':       'เปิดหุ้น',

  'wl.form.title':                'ตั้งแจ้งเตือนใหม่',
  'wl.form.titleEdit':            'แก้ไขแจ้งเตือน',
  'wl.form.update':               'บันทึกการแก้ไข',
  'wl.alert.editAria':            'แก้ไขแจ้งเตือน',
  'wl.alert.deleteAria':          'ลบแจ้งเตือน',
  'wl.alert.confirmDelete':       'ลบแจ้งเตือน {sym} ใช่ไหม?',
  'wl.form.asset':                'หุ้น / สินทรัพย์',
  'wl.form.assetPrice':           '{name} · ปัจจุบัน ${price}',
  'wl.form.condition':            'เงื่อนไข',
  'wl.form.cond.gte':             'ราคา ≥',
  'wl.form.cond.lte':             'ราคา ≤',
  'wl.form.cond.change':          '% เปลี่ยน',
  'wl.form.cond.breakoutR':       'ทะลุแนวต้าน',
  'wl.form.cond.breakoutS':       'ทะลุแนวรับ',
  'wl.form.targetPrice.gte':      'ราคาเป้าหมาย (USD) — แจ้งเมื่อ ≥',
  'wl.form.targetPrice.lte':      'ราคาเป้าหมาย (USD) — แจ้งเมื่อ ≤',
  'wl.form.changePct':            '% การเปลี่ยนแปลง (1 วัน) — แจ้งเมื่อถึง',
  'wl.form.breakoutInfo.title':   'ไม่ต้องระบุราคา',
  'wl.form.breakoutInfo.bodyResist':  'ระบบจะแจ้งเตือนอัตโนมัติเมื่อราคาทะลุแนวต้านที่คำนวณจาก technical analysis ของ Yahoo Finance',
  'wl.form.breakoutInfo.bodySupport': 'ระบบจะแจ้งเตือนอัตโนมัติเมื่อราคาทะลุลงแนวรับที่คำนวณจาก technical analysis ของ Yahoo Finance',
  'wl.form.channels':             'ช่องทางแจ้งเตือน',
  'wl.form.channel.telegram':     '✓ Telegram',
  'wl.form.channel.inapp':        '● ในแอป',
  'wl.form.save':                 'บันทึกการแจ้งเตือน',

  // Search
  'search.placeholder':           'ค้นหาหุ้น · symbol · ชื่อบริษัท',
  'search.searching':             'กำลังค้นหา…',
  'search.resultsTitle':          'ผลลัพธ์',
  'search.resultsCount':          '{n} รายการ',
  'search.notFound':              'ไม่พบ "{q}"',
  'search.recent':                'ค้นหาล่าสุด',
  'search.trending':              'นิยมค้นหาวันนี้',

  // Notifications
  'notif.title':                  'การแจ้งเตือน',
  'notif.aria.clearAll':          'ล้างทั้งหมด',
  'notif.tab.all':                'ทั้งหมด',
  'notif.tab.pending':            'รอ',
  'notif.empty':                  'ยังไม่มีการแจ้งเตือน',
  'notif.today':                  'วันนี้',
  'notif.earlier':                'เมื่อวาน / ก่อนหน้า',
  'notif.markAllRead':            'อ่านทั้งหมด',

  // Portfolio
  'portfolio.greeting':           'พอร์ตการลงทุน',
  'portfolio.title':              'Portfolio',
  'portfolio.aria.history':       'ประวัติ',
  'portfolio.aria.add':           'เพิ่ม',
  'portfolio.totalValue':         'มูลค่าพอร์ตทั้งหมด',
  'portfolio.pnl':                'กำไร/ขาดทุน',
  'portfolio.today':              'วันนี้',
  'portfolio.holdings':           'หุ้นที่ถืออยู่',
  'portfolio.holdings.count':     '{n} ตัว',
  'portfolio.addBtn':             '+ เพิ่ม',
  'portfolio.empty.title':        'ยังไม่มีหุ้นในพอร์ต',
  'portfolio.empty.hint':         'เริ่มต้นบันทึกการลงทุนเพื่อติดตามผลตอบแทน',
  'portfolio.empty.action':       'เพิ่มหุ้นตัวแรก',
  'portfolio.qtyDesc':            '{qty} หุ้น × ${avg} = ${total}',
  'portfolio.opened':             'เปิด {time}',
  'portfolio.closePosition':      'ปิด position',
  'portfolio.closePosition.confirm': 'ปิด position นี้?',
  'portfolio.recent':             'รายการล่าสุด',
  'portfolio.viewAll':            'ทั้งหมด ›',
  'portfolio.buy':                'ซื้อ',
  'portfolio.sell':               'ขาย',
  'portfolio.form.title':         'เพิ่มหุ้นในพอร์ต',
  'portfolio.form.qty':           'จำนวนหุ้น',
  'portfolio.form.avgPrice':      'ราคาเฉลี่ย (USD)',
  'portfolio.form.total':         'มูลค่ารวม',
  'portfolio.form.save':          'บันทึก position',
  'portfolio.history.title':      'ประวัติทั้งหมด',
  'portfolio.history.empty':      'ยังไม่มีรายการ',
  'portfolio.history.perShare':   '${price}/หุ้น',

  // Login / Splash
  'login.welcome':                'ยินดีต้อนรับสู่ Valryux Stocks',
  'login.feature1.title':         'ติดตามหุ้นเรียลไทม์',
  'login.feature1.body':          'US Stocks · ETF · Crypto ครบในที่เดียว',
  'login.feature2.title':         'วิเคราะห์ด้วย AI',
  'login.feature2.body':          'ขับเคลื่อนโดย NVIDIA NIM · ภาษาไทย',
  'login.feature3.title':         'แจ้งเตือนถึงราคา',
  'login.feature3.body':          'ตั้งแนวรับ/แนวต้าน · ส่งตรงเข้า Telegram',
  'login.btn.telegram':           'เข้าสู่ระบบด้วย Telegram',
  'login.btn.widget':             'เข้าสู่ระบบผ่าน Telegram',
  'login.btn.dev':                'เข้าทดลอง (Dev · localhost)',
  'login.dev.hint':               'ใช้ได้บน local เท่านั้น (ALLOW_DEV_LOGIN ที่ workers/.dev.vars)',
  'login.tos.prefix':             'เมื่อเข้าสู่ระบบ คุณยอมรับ',
  'login.tos.terms':              'เงื่อนไขการใช้งาน',
  'login.tos.and':                'และ',
  'login.tos.privacy':            'นโยบายความเป็นส่วนตัว',
  'splash.tagline':               'ดูหุ้น US · ETF · Crypto · วิเคราะห์ด้วย AI',

  // Dashboard
  'dash.greeting':                'สวัสดี',
  'dash.greeting.fallback':       'นักลงทุน',
  'dash.aria.notifications':      'แจ้งเตือน',
  'dash.section.summary':         'พอร์ตของฉัน',
  'dash.section.summary.today':   'วันนี้',
  'dash.section.summary.cta':     'เพิ่มหุ้นในพอร์ต',
  'dash.section.indices':         'ดัชนีตลาด',
  'dash.section.aiPicks':         'AI หุ้นเด่นวันนี้',
  'dash.section.sectors':         'หมวดธุรกิจ',
  'dash.section.movers':          'เคลื่อนไหวเด่นวันนี้',
  'dash.viewAll':                 'ทั้งหมด ›',
  'dash.movers.count':            '{n} หุ้น',
  'dash.movers.gain':             '↑ ขึ้น',
  'dash.movers.lose':             '↓ ลง',
  'dash.movers.empty':            'ไม่มีหุ้นในหมวดนี้',

  // Language picker
  'lang.th':              'ไทย',
  'lang.en':              'English',
  'lang.th.sub':          'Thai',
  'lang.en.sub':          'อังกฤษ',

  // Indices page
  'indices.greeting':        'ดัชนีทั่วโลก',
  'indices.title':           'Global Indices',
  'indices.region.americas': 'ทวีปอเมริกา',
  'indices.region.eu':       'ยุโรป',
  'indices.region.asia':     'เอเชีย',
  'indices.region.crypto':   'คริปโต',
  'indices.empty':           'ยังไม่มีข้อมูล',
}

const EN: typeof TH = {
  'common.all':           'All',
  'common.filter':        'Filter',
  'common.apply':         'Apply',
  'common.reset':         'Reset',
  'common.cancel':        'Cancel',
  'common.close':         'Close',
  'common.loading':       'Loading…',
  'common.empty':         'No items',
  'common.retry':         'Retry',
  'common.back':          'Back',
  'common.search':        'Search',
  'common.save':          'Save',
  'common.edit':          'Edit',
  'common.delete':        'Delete',

  'nav.home':             'Home',
  'nav.markets':          'Markets',
  'nav.watchlist':        'Watchlist',
  'nav.profile':          'Profile',

  'kind.stock':           'Stocks',
  'kind.etf':             'ETF',
  'kind.crypto':          'Crypto',
  'kind.stock.unit':      'stocks',
  'kind.etf.unit':        'ETFs',
  'kind.crypto.unit':     'coins',

  'markets.title':        'Markets',
  'markets.greeting':     'Browse the market',
  'markets.count':        '{n} {unit}',
  'markets.inSector':     'in {name}',
  'markets.sortedBy':     'sorted by {field}',
  'markets.noItems':      'No items in this sector',

  'filter.title':         'Filters',
  'filter.sort':          'Sort by',
  'filter.sort.change_desc':  '% Change (high to low)',
  'filter.sort.change_asc':   '% Change (low to high)',
  'filter.sort.marketCap':    'Market Cap',
  'filter.sort.volume':       'Volume',
  'filter.sort.price_desc':   'Price (high to low)',
  'filter.sort.price_asc':    'Price (low to high)',
  'filter.marketCap':         'Market Cap',
  'filter.marketCap.any':     'Any',
  'filter.marketCap.mega':    'Mega Cap (>$200B)',
  'filter.marketCap.large':   'Large Cap ($10B-$200B)',
  'filter.marketCap.mid':     'Mid Cap ($2B-$10B)',
  'filter.marketCap.small':   'Small Cap ($300M-$2B)',
  'filter.marketCap.micro':   'Micro Cap (<$300M)',
  'filter.change':            "Today's change",
  'filter.change.any':        'Any',
  'filter.change.gainers':    'Gainers',
  'filter.change.losers':     'Losers',
  'filter.change.big_gainers':'Big gainers (>5%)',
  'filter.change.big_losers': 'Big losers (<-5%)',
  'filter.price':             'Price range',
  'filter.price.any':         'Any',
  'filter.price.under10':     '< $10',
  'filter.price.10to100':     '$10-$100',
  'filter.price.100to500':    '$100-$500',
  'filter.price.over500':     '> $500',

  'sort.changePct':       '% change',
  'sort.marketCap':       'market cap',
  'sort.volume':          'volume',
  'sort.price':           'price',

  'profile.greeting':              'Your account',
  'profile.title':                 'Profile',
  'profile.settings':              'Settings',
  'profile.stats.favorites':       'Favorites',
  'profile.stats.alerts':          'Alerts',
  'profile.stats.portfolio':       'Portfolio',
  'profile.stats.investment':      'Investment',
  'profile.section.settings':      'Settings',
  'profile.section.account':       'Account & support',
  'profile.item.region':           'Stock region',
  'profile.item.language':         'Language',
  'profile.item.language.value':   'English',
  'profile.item.theme':            'Theme',
  'profile.item.theme.value':      'Dark',
  'profile.item.notifications':    'Notifications',
  'profile.item.notifications.value': 'Telegram · in-app',
  'profile.item.privacy':          'Privacy',
  'profile.item.privacy.value':    'Data & security',
  'settings.region.title':         'Choose stock region',
  'settings.region.hint':          'Region filters stocks · ETF/Crypto are global and not affected',

  'region.us': '🇺🇸 United States',
  'region.gb': '🇬🇧 United Kingdom',
  'region.de': '🇩🇪 Germany',
  'region.jp': '🇯🇵 Japan',
  'region.hk': '🇭🇰 Hong Kong',
  'region.ca': '🇨🇦 Canada',
  'region.au': '🇦🇺 Australia',
  'region.fr': '🇫🇷 France',
  'region.in': '🇮🇳 India',
  'region.br': '🇧🇷 Brazil',
  'region.sg': '🇸🇬 Singapore',
  'region.kr': '🇰🇷 South Korea',
  'region.tw': '🇹🇼 Taiwan',
  'region.ch': '🇨🇭 Switzerland',

  'profile.item.help':             'Help & FAQ',
  'profile.item.terms':            'Terms of Service',
  'profile.item.logout':           'Sign out',
  'profile.footer':                'Valryux Stocks v0.1.0 · powered by NVIDIA NIM + Yahoo Finance',

  'settings.title':                'Settings',
  'settings.language':             'Language',
  'settings.language.picker_title':'Choose language',
  'settings.language.picker_hint': 'Language change applies to new messages',
  'settings.theme.title':          'Choose theme',
  'settings.theme.dark':           'Dark',
  'settings.theme.light':          'Light',
  'settings.theme.system':         'System',
  'settings.theme.hint':           'Theme adapts automatically by time',
  'settings.notifications.title':  'Notifications',
  'settings.notifications.telegram':       'Telegram',
  'settings.notifications.telegram.hint':  'Messages via bot',
  'settings.notifications.inapp':          'In-app',
  'settings.notifications.inapp.hint':     'Shown in notifications page',
  'settings.privacy.title':        'Privacy',
  'settings.privacy.dataUsage':    'Data usage',
  'settings.privacy.dataUsage.body': 'This app stores only your Telegram ID and watchlist · No tracking cookies · No data sold to third parties',
  'settings.privacy.delete':       'Delete account',
  'settings.privacy.delete.body':  'Submit a deletion request with all data · processed within 7 days',
  'settings.help.title':           'Help & FAQ',
  'settings.help.q1':              'How accurate are prices?',
  'settings.help.a1':              'Data from Yahoo Finance · ~15 min delay on free tier',
  'settings.help.q2':              'Are AI insights reliable?',
  'settings.help.a2':              'AI uses Yahoo data + technical indicators · Not investment advice',
  'settings.help.q3':              'How many alerts can I set?',
  'settings.help.a3':              'Unlimited · Sent via Telegram and in-app',
  'settings.terms.title':          'Terms of Service',
  'settings.terms.body':           'By using this app you agree: (1) Data is for educational purposes only · not investment advice · (2) Developers not responsible for trading decisions · (3) Data may be delayed or incomplete · always verify with official sources',

  // Common extras
  'common.share':                 'Share',
  'common.follow':                'Follow',
  'common.unfollow':              'Unfollow',
  'common.followed':              '✓ Following',
  'common.followAdd':             '+ Follow',
  'common.history':               'History',
  'common.add':                   'Add',
  'common.clearAll':              'Clear all',
  'common.clear':                 'Clear',
  'common.or':                    'or',
  'common.viewSource':            'View source ›',
  'common.openInWeb':             'Open in browser',
  'common.source':                'Source',
  'common.refresh':               'Refresh',
  'common.thinking':              'Thinking…',

  'month.1':  'January', 'month.2':  'February', 'month.3':  'March',
  'month.4':  'April',   'month.5':  'May',      'month.6':  'June',
  'month.7':  'July',    'month.8':  'August',   'month.9':  'September',
  'month.10': 'October', 'month.11': 'November', 'month.12': 'December',

  'weekday.0': 'Sun', 'weekday.1': 'Mon', 'weekday.2': 'Tue', 'weekday.3': 'Wed',
  'weekday.4': 'Thu', 'weekday.5': 'Fri', 'weekday.6': 'Sat',

  'cal.greeting':         'Earnings calendar',
  'cal.title':            'Earnings Calendar',
  'cal.entryTitle':       'Earnings Calendar',
  'cal.entrySubtitle':    'Reporting dates for your watchlist + portfolio',
  'cal.source.all':       'All',
  'cal.source.watchlist': 'Watchlist',
  'cal.source.portfolio': 'Portfolio',
  'cal.rangeDays':        'Next {n} days',
  'cal.today':            'Today',
  'cal.tomorrow':         'Tomorrow',
  'cal.yesterday':        'Yesterday',
  'cal.inDays':           'In {n} days',
  'cal.daysAgo':          '{n} days ago',
  'cal.empty':            'No upcoming reports in this window',
  'cal.emptyHint':        'Add stocks to your watchlist or open a position',
  'cal.summary':          '{n} reports in the next {days} days',

  'stock.aria.share':             'Share',
  'stock.aria.alarm':             'Set alert',
  'stock.setAlert':               'Set alert',
  'stock.priceUnavailable':       'Price data unavailable · please refresh',
  'stock.stat.open':              'Open',
  'stock.stat.high':              'High',
  'stock.stat.low':               'Low',
  'stock.stat.preMarket':         'Pre-market',
  'stock.stat.postMarket':        'After-hours',
  'stock.stat.marketClosed':      'Closed',
  'stock.stat.realtime':          'Real-time',

  'stock.tab.chart':              'Chart',
  'stock.tab.company':            'Company',
  'stock.tab.financials':         'Financials',
  'stock.tab.earning':            'Earnings',
  'stock.tab.news':               'News',

  'stock.score.title':            'Yahoo Finance Score',
  'stock.score.loading':          'Loading score from Yahoo Finance…',
  'stock.score.empty':            'No score available for {sym}',
  'stock.score.growth':           'Growth',
  'stock.score.value':            'Value',
  'stock.score.momentum':         'Momentum',
  'stock.score.quality':          'Quality',
  'stock.score.sentiment':        'Sentiment',
  'stock.rec.strong_buy':         'Strong buy',
  'stock.rec.buy':                'Buy',
  'stock.rec.hold':               'Hold',
  'stock.rec.sell':               'Sell',
  'stock.rec.strong_sell':        'Strong sell',

  'stock.chart.loading':          'Loading chart…',
  'stock.chart.empty':            'Chart unavailable',

  'stock.ai.title':               'AI Analysis',
  'stock.ai.summary':             'AI Summary',
  'stock.ai.emptyHint':           'No analysis yet for {sym} · tap "↻ Refresh" to ask AI',
  'stock.ai.streaming':           'AI is analyzing…',
  'stock.ai.source':              'Source: Yahoo Finance · NVIDIA NIM ·',
  'stock.ai.disclaimer':          'Analysis is not investment advice',

  'stock.company.empty':          'Company data unavailable',
  'stock.company.ceo':            'CEO',
  'stock.company.founded':        'Founded',
  'stock.company.hq':             'Headquarters',
  'stock.company.employees':      'Employees',
  'stock.company.exchange':       'Exchange',
  'stock.company.sector':         'Sector',
  'stock.company.industry':       'Industry',
  'stock.company.marketCap':      'Market Cap',
  'stock.company.tagsTitle':      'Categories / Industry',
  'stock.company.shareholders':   'Major shareholders',

  'stock.fin.empty':              'Financial data unavailable',
  'stock.fin.revenue':            'Revenue',
  'stock.fin.eps':                'EPS',
  'stock.fin.grossMargin':        'Gross Margin',
  'stock.fin.opMargin':           'Op Margin',
  'stock.fin.history':            'Last 4 quarters revenue',
  'stock.fin.income':             'Income statement',
  'stock.fin.grossProfit':        'Gross profit',
  'stock.fin.netIncome':          'Net income',

  'stock.earn.empty':             'Earnings data unavailable',
  'stock.earn.next':              'Next earnings',
  'stock.earn.latest':            'Latest earnings',
  'stock.earn.daysUnit':          'days',
  'stock.earn.passedAgo':         '{n} days ago',
  'stock.earn.last4q':            'Last {n} quarters',
  'stock.earn.consensus':         'Analyst Consensus',
  'stock.earn.consensus.nodes':   '· {n} analysts',
  'stock.earn.targetMean':        'Mean target price',
  'stock.earn.upside':            '{pct}% upside',
  'stock.earn.buyPct':            'Buy {pct}%',
  'stock.earn.holdPct':           'Hold {pct}%',
  'stock.earn.sellPct':           'Sell {pct}%',
  'stock.fin.quarterly':          'Quarterly',
  'stock.fin.annual':             'Annual',
  'stock.fin.trend':              'Trend',
  'stock.fin.forecast':           'Forecast',
  'stock.fin.estimateLabel':      'Analyst average',
  'stock.earn.beatsMisses':       'Beats / Misses history',
  'stock.earn.beat':              'Beat',
  'stock.earn.miss':              'Miss',
  'stock.earn.confirmed':         'Confirmed',
  'stock.earn.estimated':         'Estimated',
  'stock.earn.analystCount':      '{n} analysts',
  'stock.analyst.recentRatings':  'Recent Analyst Ratings',

  'stock.news.sentiment.bull':    'Positive',
  'stock.news.sentiment.bear':    'Negative',
  'stock.news.sentiment.neutral': 'Neutral',
  'stock.news.sentiment.summary': '{tone} sentiment · {n} articles',
  'stock.news.sentiment.breakdown': '{pos} positive · {neu} neutral · {neg} negative',
  'stock.news.filter.all':        'All',
  'stock.news.filter.positive':   'Positive',
  'stock.news.filter.neutral':    'Neutral',
  'stock.news.filter.negative':   'Negative',
  'stock.news.empty':             'No news yet',
  'stock.news.emptyFilter':       'No {tone} news',

  'stock.article.loading':        'Loading article…',
  'stock.article.paywall':        'This article requires a subscription (paywall)',
  'stock.article.unreadable':     'Unable to read in app',
  'stock.article.failed':         'Failed to load article',

  'wl.greeting':                  'Your watchlist',
  'wl.title':                     'Watchlist',
  'wl.aria.filter':               'Filter',
  'wl.aria.add':                  'Add',
  'wl.alertsActive':              'Active alerts',
  'wl.alertsTotal':               'of {n}',
  'wl.tab.favorites':             'Favorites',
  'wl.tab.alerts':                'Alerts',
  'wl.favorites.empty':           'No favorites yet',
  'wl.alerts.active':             'Active',
  'wl.alerts.inactive':           'Inactive',
  'wl.alerts.countItems':         '{n} items',
  'wl.alert.target':              'Target',
  'wl.alert.current':             'Current',
  'wl.alert.near':                'Near trigger',
  'wl.alert.distance':            'Distance to target',
  'wl.alert.closedTarget':        'Target {v} · disabled',
  'wl.alert.cond.gte':            'Price ≥',
  'wl.alert.cond.lte':            'Price ≤',
  'wl.alert.cond.change':         '% change',
  'wl.alert.cond.breakoutR':      'Break R',
  'wl.alert.cond.breakoutS':      'Break S',
  'wl.alert.text.autoResist':     'Auto-resistance',
  'wl.alert.text.autoSupport':    'Auto-support',
  'wl.alert.text.perDay':         '{v}% / day',
  'notif.detail.title':           'Notification details',
  'notif.detail.alertOn':         'Active',
  'notif.detail.alertOff':        'Off',
  'notif.detail.manage':          'Manage alert',
  'notif.detail.openStock':       'Open stock',

  'wl.form.title':                'New alert',
  'wl.form.titleEdit':            'Edit alert',
  'wl.form.update':               'Save changes',
  'wl.alert.editAria':            'Edit alert',
  'wl.alert.deleteAria':          'Delete alert',
  'wl.alert.confirmDelete':       'Delete alert for {sym}?',
  'wl.form.asset':                'Stock / asset',
  'wl.form.assetPrice':           '{name} · current ${price}',
  'wl.form.condition':            'Condition',
  'wl.form.cond.gte':             'Price ≥',
  'wl.form.cond.lte':             'Price ≤',
  'wl.form.cond.change':          '% change',
  'wl.form.cond.breakoutR':       'Break resistance',
  'wl.form.cond.breakoutS':       'Break support',
  'wl.form.targetPrice.gte':      'Target price (USD) — alert when ≥',
  'wl.form.targetPrice.lte':      'Target price (USD) — alert when ≤',
  'wl.form.changePct':            '% change (1 day) — alert when reaching',
  'wl.form.breakoutInfo.title':   'No price needed',
  'wl.form.breakoutInfo.bodyResist':  'System triggers automatically when price breaks above resistance computed from Yahoo Finance technical analysis',
  'wl.form.breakoutInfo.bodySupport': 'System triggers automatically when price breaks below support computed from Yahoo Finance technical analysis',
  'wl.form.channels':             'Notification channels',
  'wl.form.channel.telegram':     '✓ Telegram',
  'wl.form.channel.inapp':        '● In-app',
  'wl.form.save':                 'Save alert',

  'search.placeholder':           'Search stocks · symbol · company',
  'search.searching':             'Searching…',
  'search.resultsTitle':          'Results',
  'search.resultsCount':          '{n} items',
  'search.notFound':              'No results for "{q}"',
  'search.recent':                'Recent searches',
  'search.trending':              'Trending searches',

  'notif.title':                  'Notifications',
  'notif.aria.clearAll':          'Clear all',
  'notif.tab.all':                'All',
  'notif.tab.pending':            'Pending',
  'notif.empty':                  'No notifications yet',
  'notif.today':                  'Today',
  'notif.earlier':                'Yesterday / earlier',
  'notif.markAllRead':            'Mark all read',

  'portfolio.greeting':           'Investment portfolio',
  'portfolio.title':              'Portfolio',
  'portfolio.aria.history':       'History',
  'portfolio.aria.add':           'Add',
  'portfolio.totalValue':         'Total portfolio value',
  'portfolio.pnl':                'P&L',
  'portfolio.today':              'Today',
  'portfolio.holdings':           'Holdings',
  'portfolio.holdings.count':     '{n} positions',
  'portfolio.addBtn':             '+ Add',
  'portfolio.empty.title':        'No positions yet',
  'portfolio.empty.hint':         'Start tracking your investments to see returns',
  'portfolio.empty.action':       'Add first stock',
  'portfolio.qtyDesc':            '{qty} shares × ${avg} = ${total}',
  'portfolio.opened':             'Opened {time}',
  'portfolio.closePosition':      'Close position',
  'portfolio.closePosition.confirm': 'Close this position?',
  'portfolio.recent':             'Recent transactions',
  'portfolio.viewAll':            'View all ›',
  'portfolio.buy':                'Buy',
  'portfolio.sell':               'Sell',
  'portfolio.form.title':         'Add to portfolio',
  'portfolio.form.qty':           'Shares',
  'portfolio.form.avgPrice':      'Average price (USD)',
  'portfolio.form.total':         'Total value',
  'portfolio.form.save':          'Save position',
  'portfolio.history.title':      'Full history',
  'portfolio.history.empty':      'No transactions yet',
  'portfolio.history.perShare':   '${price}/share',

  'login.welcome':                'Welcome to Valryux Stocks',
  'login.feature1.title':         'Real-time tracking',
  'login.feature1.body':          'US Stocks · ETF · Crypto all in one',
  'login.feature2.title':         'AI analysis',
  'login.feature2.body':          'Powered by NVIDIA NIM',
  'login.feature3.title':         'Price alerts',
  'login.feature3.body':          'Set support/resistance · delivered to Telegram',
  'login.btn.telegram':           'Sign in with Telegram',
  'login.btn.widget':             'Sign in via Telegram',
  'login.btn.dev':                'Try demo (Dev · localhost)',
  'login.dev.hint':               'Works on local only (ALLOW_DEV_LOGIN in workers/.dev.vars)',
  'login.tos.prefix':             'By signing in, you accept the',
  'login.tos.terms':              'Terms of Service',
  'login.tos.and':                'and',
  'login.tos.privacy':            'Privacy Policy',
  'splash.tagline':               'US Stocks · ETF · Crypto · AI-powered',

  'dash.greeting':                'Hello',
  'dash.greeting.fallback':       'investor',
  'dash.aria.notifications':      'Notifications',
  'dash.section.summary':         'My Portfolio',
  'dash.section.summary.today':   'Today',
  'dash.section.summary.cta':     'Add positions to portfolio',
  'dash.section.indices':         'Market indices',
  'dash.section.aiPicks':         "Today's AI picks",
  'dash.section.sectors':         'Sectors',
  'dash.section.movers':          "Today's movers",
  'dash.viewAll':                 'View all ›',
  'dash.movers.count':            '{n} stocks',
  'dash.movers.gain':             '↑ Gainers',
  'dash.movers.lose':             '↓ Losers',
  'dash.movers.empty':            'No stocks in this sector',

  'lang.th':              'ไทย',
  'lang.en':              'English',
  'lang.th.sub':          'Thai',
  'lang.en.sub':          'English',

  // Indices page
  'indices.greeting':        'Global indices',
  'indices.title':           'Global Indices',
  'indices.region.americas': 'Americas',
  'indices.region.eu':       'Europe',
  'indices.region.asia':     'Asia',
  'indices.region.crypto':   'Crypto',
  'indices.empty':           'No data',
}

export type I18nKey = keyof typeof TH
type TFn = (key: I18nKey, vars?: Record<string, string | number>) => string

export function useT(): ComputedRef<TFn> {
  const prefs = usePrefsStore()
  return computed<TFn>(() => {
    const dict = prefs.language === 'en' ? EN : TH
    return (key, vars) => {
      let s: string = dict[key] ?? EN[key] ?? key
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          s = s.replace(`{${k}}`, String(v))
        }
      }
      return s
    }
  })
}

/** Non-reactive helper · ใช้นอก setup() (e.g., utilities) */
export function tStatic(key: I18nKey, vars?: Record<string, string | number>): string {
  const prefs = usePrefsStore()
  const dict = prefs.language === 'en' ? EN : TH
  let s: string = dict[key] ?? EN[key] ?? key
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(`{${k}}`, String(v))
    }
  }
  return s
}
